import { Calendar as CalendarIcon, CircleX } from "lucide-react";
import { DateTime } from "luxon";
import * as React from "react";

import { cn } from "@/lib/utils";
import { useMask } from "@react-input/mask";
import { type Matcher, SelectSingleEventHandler } from "react-day-picker";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";

/**
 * `@react-input/mask` tracks the caret with a `setTimeout` loop started on focus and stopped only
 * on blur. Removing a focused input does not blur it under jsdom, so the loop outlives the test
 * environment. Blur while the node is still mounted.
 */
function MaskedDateInput({ inputRef, ...props }: React.ComponentProps<typeof Input> & { inputRef: React.MutableRefObject<HTMLInputElement | null> }) {
    React.useLayoutEffect(() => {
        const node = inputRef.current;
        return () => node?.blur();
    }, [inputRef]);
    return <Input ref={inputRef} {...props} />;
}

interface DateTimePickerProps {
    /** Id of the button, so a form label points at it. */
    id?: string;
    date?: DateTime;
    setDate?: (date?: DateTime) => void;
    placeholder?: string;
    className?: string;
    allowClear?: boolean;
    disabled?: boolean;
    enableWeekend?: boolean;
    /**
     * Limit selectable dates to a specific weekday (1 = Monday, 7 = Sunday).
     * When set, both the calendar and manual input will only accept this weekday.
     */
    allowedWeekday?: number;
    /** Disable dates before this date (exclusive) */
    minDate?: DateTime;
    /** Disable dates after this date (exclusive) */
    maxDate?: DateTime;
}

export function DatePicker({ id, date, setDate, placeholder = "Select a date", className, allowClear, disabled, enableWeekend, allowedWeekday, minDate, maxDate }: DateTimePickerProps) {
    const [open, setOpen] = React.useState(false);
    const [selectedDate, setSelectedDate] = React.useState<DateTime | undefined>(date);
    const [month, setMonth] = React.useState<Date | undefined>(date?.toJSDate());
    const [inputValue, setInputValue] = React.useState(date?.toFormat("dd/MM/yyyy"));
    const inputRef = useMask({ mask: "__/__/____", replacement: { _: /\d/ }, showMask: true });

    // Synced from `date` when its day changes, not when a parent renders a new `DateTime` of the same day
    // (a form re-rendering on every keystroke) nor when it only echoes what this picker emitted: a date
    // half typed, emitted as nothing, would otherwise wipe the input being typed.
    const dateKey = date?.isValid ? date.toISODate() : null;
    const emitted = React.useRef<string | null | undefined>(undefined);
    const emit = (value?: DateTime) => {
        emitted.current = value?.isValid ? value.toISODate() : null;
        setDate?.(value);
    };
    React.useEffect(() => {
        if (emitted.current !== undefined && emitted.current === dateKey) return;
        emitted.current = undefined;
        setSelectedDate(date);
        setInputValue(date?.toFormat("dd/MM/yyyy") || "");
        if (date?.isValid) {
            setMonth(date.toJSDate());
        }
    }, [dateKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const isDateInRange = (d: DateTime) => {
        if (minDate && d.startOf("day") < minDate.startOf("day")) return false;
        if (maxDate && d.startOf("day") > maxDate.startOf("day")) return false;
        return true;
    };

    const handleSelect: SelectSingleEventHandler = (_day, selected) => {
        const selectedDay = DateTime.fromJSDate(selected);

        if (allowedWeekday && selectedDay.weekday !== allowedWeekday) {
            // Ignore selections that don't match the allowed weekday
            return;
        }
        if (!isDateInRange(selectedDay)) return;

        setSelectedDate(selectedDay);
        setInputValue(selectedDay.toFormat("dd/MM/yyyy"));
        emit(selectedDay);
    };

    const disabledMatchers: Matcher[] = [];
    if (allowedWeekday && allowedWeekday >= 1 && allowedWeekday <= 7) {
        disabledMatchers.push({
            dayOfWeek: [0, 1, 2, 3, 4, 5, 6].filter(d => {
                const luxonWeekday = d === 0 ? 7 : d;
                return luxonWeekday !== allowedWeekday;
            })
        });
    }
    if (minDate) disabledMatchers.push({ before: minDate.toJSDate() });
    if (maxDate) disabledMatchers.push({ after: maxDate.toJSDate() });
    const disabledDays: Matcher[] | undefined = disabledMatchers.length > 0 ? disabledMatchers : undefined;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <div>
                    {!open ? (
                        <Button
                            id={id}
                            type="button"
                            variant={"outline"}
                            className={cn("items-center justify-between text-left font-normal", !selectedDate && "text-muted-foreground", className)}
                            onClick={() => {
                                // Opened on the date held: not on a date cleared or half typed before.
                                setInputValue(selectedDate?.toFormat("dd/MM/yyyy") ?? "");
                                setOpen(true);
                            }}
                            disabled={disabled}
                        >
                            <span className="flex justify-start">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {selectedDate ? selectedDate.toFormat("DDD") : <span>{placeholder}</span>}
                            </span>
                            <span>
                                {allowClear && selectedDate && (
                                    <CircleX
                                        className="h-4 w-4 cursor-pointer"
                                        onClick={e => {
                                            setSelectedDate(undefined);
                                            emit(undefined);
                                            e.stopPropagation();
                                        }}
                                    />
                                )}
                            </span>
                        </Button>
                    ) : (
                        <MaskedDateInput
                            inputRef={inputRef}
                            value={inputValue}
                            onChange={e => {
                                setInputValue(e.target.value);
                                try {
                                    const modifiedDay = DateTime.fromFormat(e.target.value, "dd/MM/yyyy");
                                    const validWeekday = !allowedWeekday || modifiedDay.weekday === allowedWeekday;
                                    const validRange = isDateInRange(modifiedDay);
                                    if (modifiedDay.isValid && validWeekday && validRange) {
                                        setSelectedDate(modifiedDay);
                                        setMonth(modifiedDay.toJSDate());
                                        emit(modifiedDay);
                                    } else {
                                        setSelectedDate(undefined);
                                        if (allowedWeekday && modifiedDay.isValid && modifiedDay.weekday !== allowedWeekday) {
                                            setInputValue("");
                                        }
                                        emit(undefined);
                                    }
                                } catch (error) {
                                    console.error(error);
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    setOpen(false);
                                }
                            }}
                            autoFocus
                        />
                    )}
                </div>
            </PopoverAnchor>
            <PopoverContent
                className="w-auto p-0"
                align="start"
                onOpenAutoFocus={e => e.preventDefault()}
                onInteractOutside={e => {
                    if (e.target === inputRef.current) e.preventDefault();
                }}
            >
                <Calendar mode="single" selected={selectedDate?.toJSDate()} onSelect={handleSelect} month={month} onMonthChange={setMonth} enableWeekend={enableWeekend} disabled={disabledDays} />
            </PopoverContent>
        </Popover>
    );
}
